import React, { useState, useCallback, useContext, createContext, useEffect, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Handle,
  Position,
  useReactFlow,
  MarkerType,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// ─── Shared context so node components can read templates ─────────────────────
const TemplatesCtx = createContext([]);

// ─── Condition rules per channel ──────────────────────────────────────────────
const COND_RULES = {
  email:    [
    { v: 'abriu',       l: 'Abriu o e-mail' },
    { v: 'clicou_link', l: 'Clicou num link' },
    { v: 'respondeu',   l: 'Respondeu' },
  ],
  linkedin: [
    { v: 'conectou',   l: 'Aceitou conexão' },
    { v: 'respondeu',  l: 'Respondeu mensagem' },
  ],
  whatsapp: [
    { v: 'respondeu', l: 'Respondeu' },
    { v: 'leu',       l: 'Leu a mensagem' },
  ],
  sms: [
    { v: 'respondeu', l: 'Respondeu' },
    { v: 'clicou_link', l: 'Clicou no link' },
  ],
};

// ─── Integration required per node type ───────────────────────────────────────
const INTEGRATION_REQUIRED = {
  linkedin: 'linkedin',
  whatsapp: 'whatsapp',
  sms: 'twilio',
};

// ─── Sidebar items ────────────────────────────────────────────────────────────
const SIDEBAR_ITEMS = [
  { type: 'email',     label: 'E-mail',    icon: '📧', accent: '#3b82f6' },
  { type: 'linkedin',  label: 'LinkedIn',  icon: '💼', accent: '#6366f1' },
  { type: 'whatsapp',  label: 'WhatsApp',  icon: '📱', accent: '#22c55e' },
  { type: 'sms',       label: 'SMS',       icon: '💬', accent: '#ef4444' },
  { type: 'wait',      label: 'Aguardar',  icon: '⏳', accent: '#f59e0b' },
  { type: 'condition', label: 'Condição',  icon: '🔀', accent: '#8b5cf6' },
  { type: 'endNode',   label: 'Encerrar',  icon: '🔴', accent: '#ef4444' },
];

// ─── Common handle styles ─────────────────────────────────────────────────────
const H = { width: 10, height: 10, border: '2px solid #fff', borderRadius: '50%', background: '#94a3b8' };

// ─── START Node ───────────────────────────────────────────────────────────────
function StartNode() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{
        padding: '6px 28px', background: '#1e293b', color: '#fff',
        borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
        boxShadow: '0 3px 10px rgba(0,0,0,0.25)', userSelect: 'none',
      }}>
        INÍCIO
      </div>
      <Handle type="source" position={Position.Bottom} style={{ ...H, background: '#475569' }} />
    </div>
  );
}

// ─── END Node ─────────────────────────────────────────────────────────────────
function EndNode() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Handle type="target" position={Position.Top} style={H} />
      <div style={{
        padding: '6px 28px', background: '#ef4444', color: '#fff',
        borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
        boxShadow: '0 3px 10px rgba(239,68,68,0.35)', userSelect: 'none',
      }}>
        ENCERRAR
      </div>
    </div>
  );
}

// ─── ACTION Node (email / linkedin / whatsapp / sms) ─────────────────────────
const ACTION_CFG = {
  email:    { label: 'E-mail',   icon: '📧', hdr: '#2563eb', border: '#93c5fd' },
  linkedin: { label: 'LinkedIn', icon: '💼', hdr: '#4f46e5', border: '#a5b4fc' },
  whatsapp: { label: 'WhatsApp', icon: '📱', hdr: '#16a34a', border: '#86efac' },
  sms:      { label: 'SMS',      icon: '💬', hdr: '#dc2626', border: '#fca5a5' },
};

function ActionNode({ id, data, selected }) {
  const { updateNodeData } = useReactFlow();
  const templates = useContext(TemplatesCtx);
  const cfg = ACTION_CFG[data.nodeType] || ACTION_CFG.email;
  const filtered = templates.filter(t => t.channel === data.nodeType);

  return (
    <div style={{
      minWidth: 210, borderRadius: 12, background: '#fff', overflow: 'hidden',
      border: `2px solid ${selected ? '#8b5cf6' : cfg.border}`,
      boxShadow: selected ? '0 0 0 3px #ede9fe, 0 4px 12px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <Handle type="target" position={Position.Top} style={H} />
      <div style={{ background: cfg.hdr, color: '#fff', padding: '7px 12px', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 14 }}>{cfg.icon}</span>
        {cfg.label}
      </div>
      <div style={{ padding: '10px 12px' }}>
        <select
          value={data.template_id || ''}
          onChange={e => updateNodeData(id, { template_id: e.target.value })}
          onClick={e => e.stopPropagation()}
          style={{ width: '100%', fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 8px', background: '#f8fafc', outline: 'none', cursor: 'pointer' }}
        >
          <option value="">— Selecionar template —</option>
          {filtered.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {filtered.length === 0 && (
          <p style={{ fontSize: 10, color: '#f59e0b', marginTop: 4 }}>
            ⚠ Nenhum template {cfg.label} criado ainda
          </p>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={H} />
    </div>
  );
}

// ─── WAIT Node ────────────────────────────────────────────────────────────────
function WaitNode({ id, data, selected }) {
  const { updateNodeData } = useReactFlow();

  return (
    <div style={{
      minWidth: 190, borderRadius: 12, background: '#fff', overflow: 'hidden',
      border: `2px solid ${selected ? '#8b5cf6' : '#fcd34d'}`,
      boxShadow: selected ? '0 0 0 3px #ede9fe, 0 4px 12px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <Handle type="target" position={Position.Top} style={H} />
      <div style={{ background: '#d97706', color: '#fff', padding: '7px 12px', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 14 }}>⏳</span> Aguardar
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, color: '#64748b' }}>Esperar</span>
        <input
          type="number" min="1" max="90"
          value={data.wait_days ?? 1}
          onChange={e => updateNodeData(id, { wait_days: Number(e.target.value) })}
          onClick={e => e.stopPropagation()}
          style={{ width: 52, textAlign: 'center', fontSize: 14, fontWeight: 700, border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 6px', outline: 'none' }}
        />
        <span style={{ fontSize: 11, color: '#64748b' }}>dia(s)</span>
      </div>
      <Handle type="source" position={Position.Bottom} style={H} />
    </div>
  );
}

// ─── CONDITION Node ────────────────────────────────────────────────────────────
function ConditionNode({ id, data, selected }) {
  const { updateNodeData } = useReactFlow();
  const canal = data.canal || 'email';
  const rules = COND_RULES[canal] || [];

  return (
    <div style={{
      minWidth: 230, borderRadius: 12, background: '#fff', overflow: 'visible',
      border: `2px solid ${selected ? '#8b5cf6' : '#c4b5fd'}`,
      boxShadow: selected ? '0 0 0 3px #ede9fe, 0 4px 12px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <Handle type="target" position={Position.Top} style={H} />
      <div style={{ background: '#7c3aed', color: '#fff', padding: '7px 12px', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, borderRadius: '10px 10px 0 0' }}>
        <span style={{ fontSize: 14 }}>🔀</span> Condição
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <select
          value={canal}
          onChange={e => updateNodeData(id, { canal: e.target.value, regra: '' })}
          onClick={e => e.stopPropagation()}
          style={{ width: '100%', fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 8px', background: '#f8fafc', outline: 'none', cursor: 'pointer' }}
        >
          <option value="email">📧 E-mail</option>
          <option value="linkedin">💼 LinkedIn</option>
          <option value="whatsapp">📱 WhatsApp</option>
          <option value="sms">💬 SMS</option>
        </select>
        <select
          value={data.regra || ''}
          onChange={e => updateNodeData(id, { regra: e.target.value })}
          onClick={e => e.stopPropagation()}
          style={{ width: '100%', fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 8px', background: '#f8fafc', outline: 'none', cursor: 'pointer' }}
        >
          <option value="">— Selecionar condição —</option>
          {rules.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
          <span>Verificar por</span>
          <input
            type="number" min="1" max="30"
            value={data.verificar_por_dias ?? 2}
            onChange={e => updateNodeData(id, { verificar_por_dias: Number(e.target.value) })}
            onClick={e => e.stopPropagation()}
            style={{ width: 44, textAlign: 'center', fontSize: 12, fontWeight: 700, border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 5px', outline: 'none' }}
          />
          <span>dias</span>
        </div>
        {/* Sim / Não labels above handles */}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 6, paddingTop: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: '#ef4444', paddingLeft: 12 }}>← Não</span>
          <span style={{ fontSize: 9, fontWeight: 800, color: '#22c55e', paddingRight: 12 }}>Sim →</span>
        </div>
      </div>
      {/* Left (No) and Right (Yes) handles */}
      <Handle
        type="source" position={Position.Bottom} id="no"
        style={{ ...H, background: '#ef4444', left: '25%', transform: 'none', bottom: -5 }}
      />
      <Handle
        type="source" position={Position.Bottom} id="yes"
        style={{ ...H, background: '#22c55e', left: '75%', transform: 'none', bottom: -5 }}
      />
    </div>
  );
}

// ─── nodeTypes — MUST be outside component to avoid React Flow re-registration ─
const nodeTypes = {
  startNode:  StartNode,
  endNode:    EndNode,
  email:      ActionNode,
  linkedin:   ActionNode,
  whatsapp:   ActionNode,
  sms:        ActionNode,
  wait:       WaitNode,
  condition:  ConditionNode,
};

// ─── Default edge style ────────────────────────────────────────────────────────
const defaultEdgeOptions = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8', width: 18, height: 18 },
  style: { stroke: '#94a3b8', strokeWidth: 2 },
};

// ─── Initial nodes (start only) ───────────────────────────────────────────────
const INITIAL_NODES = [
  { id: 'start', type: 'startNode', position: { x: 220, y: 30 }, data: {}, deletable: false },
];

// ─── Sidebar draggable item ───────────────────────────────────────────────────
function SidebarItem({ item, disabled }) {
  const [hovered, setHovered] = useState(false);
  const onDragStart = (e) => {
    if (disabled) { e.preventDefault(); return; }
    e.dataTransfer.setData('application/reactflow', item.type);
    e.dataTransfer.effectAllowed = 'move';
  };

  if (disabled) {
    return (
      <div
        title="Integração não configurada — configure em Integrações Gerais"
        style={{
          padding: '9px 12px', borderRadius: 10, cursor: 'not-allowed',
          border: '1.5px solid #e2e8f0', background: '#f8fafc',
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 12, fontWeight: 600, color: '#94a3b8',
          userSelect: 'none', opacity: 0.55,
        }}
      >
        <span style={{ fontSize: 16, filter: 'grayscale(1)' }}>{item.icon}</span>
        <span>{item.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 9, color: '#f59e0b', fontWeight: 700 }}>⚠</span>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '9px 12px', borderRadius: 10, cursor: 'grab',
        border: `1.5px solid ${hovered ? item.accent : '#e2e8f0'}`,
        background: hovered ? `${item.accent}10` : '#f8fafc',
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12, fontWeight: 600, color: '#374151',
        userSelect: 'none', transition: 'all 0.12s',
      }}
    >
      <span style={{ fontSize: 16 }}>{item.icon}</span>
      <span>{item.label}</span>
    </div>
  );
}

// ─── Inner canvas (must be child of ReactFlowProvider) ───────────────────────
function FlowCanvas({ initialNodes, initialEdges, onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { screenToFlowPosition } = useReactFlow();
  const wrapperRef = useRef(null);

  // Notify parent on changes (debounced slightly to avoid flooding)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current({ nodes, edges });
  }, [nodes, edges]);

  const onConnect = useCallback((params) => {
    const isNo  = params.sourceHandle === 'no';
    const isYes = params.sourceHandle === 'yes';
    setEdges(eds => addEdge({
      ...params,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18,
        color: isNo ? '#ef4444' : isYes ? '#22c55e' : '#94a3b8' },
      style: { stroke: isNo ? '#ef4444' : isYes ? '#22c55e' : '#94a3b8', strokeWidth: 2 },
      label: isNo ? 'Não' : isYes ? 'Sim' : undefined,
      labelStyle: { fill: isNo ? '#ef4444' : '#22c55e', fontSize: 10, fontWeight: 700 },
      labelBgStyle: { fill: '#fff', fillOpacity: 0.8 },
    }, eds));
  }, [setEdges]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('application/reactflow');
    if (!nodeType) return;
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });

    const defaultData = {
      email:     { nodeType: 'email',    template_id: '' },
      linkedin:  { nodeType: 'linkedin', template_id: '' },
      whatsapp:  { nodeType: 'whatsapp', template_id: '' },
      sms:       { nodeType: 'sms',      template_id: '' },
      wait:      { wait_days: 1 },
      condition: { canal: 'email', regra: '', verificar_por_dias: 2 },
      endNode:   {},
    };

    setNodes(nds => [...nds, {
      id: `${nodeType}-${Date.now()}`,
      type: nodeType,
      position,
      data: defaultData[nodeType] || {},
    }]);
  }, [screenToFlowPosition, setNodes]);

  return (
    <div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        deleteKeyCode={['Backspace', 'Delete']}
        fitView
        fitViewOptions={{ padding: 0.3, maxZoom: 1.2 }}
        minZoom={0.3}
        maxZoom={2}
      >
        <Background color="#e2e8f0" gap={20} size={1} />
        <Controls showInteractive={false} />
        <Panel position="top-left" style={{ fontSize: 10, color: '#94a3b8', background: 'rgba(255,255,255,0.85)', padding: '4px 8px', borderRadius: 6 }}>
          Arraste os nós do painel • Conecte puxando a bolinha inferior • Del para remover
        </Panel>
      </ReactFlow>
    </div>
  );
}

// ─── Public export ─────────────────────────────────────────────────────────────
export default function CampaignFlowBuilder({ flowData, onChange, templates, integrationStatus = {} }) {
  const initNodes = flowData?.nodes?.length > 0 ? flowData.nodes : INITIAL_NODES;
  const initEdges = flowData?.edges  || [];

  const isDisabled = (type) => {
    const req = INTEGRATION_REQUIRED[type];
    if (!req) return false;
    return !integrationStatus[req];
  };

  return (
    <div style={{ display: 'flex', height: '100%', background: '#f1f5f9', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      {/* Canvas */}
      <TemplatesCtx.Provider value={templates || []}>
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlowProvider>
            <FlowCanvas
              initialNodes={initNodes}
              initialEdges={initEdges}
              onChange={onChange}
            />
          </ReactFlowProvider>
        </div>
      </TemplatesCtx.Provider>

      {/* Right Sidebar */}
      <div style={{
        width: 148, background: '#fff', borderLeft: '1px solid #e2e8f0',
        padding: '14px 10px', display: 'flex', flexDirection: 'column', gap: 8,
        overflowY: 'auto',
      }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
          Arrastar para o fluxo
        </p>
        {SIDEBAR_ITEMS.map(item => (
          <SidebarItem key={item.type} item={item} disabled={isDisabled(item.type)} />
        ))}
        <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
          <p style={{ fontSize: 9, color: '#cbd5e1', textAlign: 'center', lineHeight: 1.5 }}>
            Selecione um nó e pressione<br/>Del para remover
          </p>
        </div>
      </div>
    </div>
  );
}
